import { ExternalService, NomenclatureItem } from '../models/core.models';
import { Observable, of, tap } from 'rxjs';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Injectable, inject } from '@angular/core';
import { UserApiService } from 'src/app/api/services/user-api.service';

export enum WebSocketMessageType {
  MESSAGE_SUBSCRIBE_TASK = 'subscribeTask',
  MESSAGE_SUBSCRIBE_USER = 'subscribeUser',
  MESSAGE_TASK_PROGRESS = 'taskProgress',
  MESSAGE_SEND_NOTIFICATION = 'sendNotification',
  MESSAGE_PING = 'ping',
  MESSAGE_PONG = 'pong',
}

export enum UserAccessLevel {
  /**
   * Level 1: Guest
   * Initial State (Guest / Invited?)
   */
  GUEST = 1,

  /**
   * Level 10: Client User
   * Regular client user
   */
  CLIENT_USER = 10,

  /**
   * Level 20: Client Admin
   * Regular client admin (can access company management tools, subject to Feature Permissions)
   */
  CLIENT_ADMIN = 20,

  /**
   * Level 30: Agency User
   * Regular agency user (like Client User, but with the option to have multiple client companies for support)
   */
  AGENCY_USER = 30,

  /**
   * Level 40: Agency Admin
   * Agency client admin (can access agency’s company management tools, subject to Feature Permissions)
   */
  AGENCY_ADMIN = 40,

  /**
   * Level 50: Global Admin
   * Sensika admin (can access all companies' management tools, subject to Feature Permissions)
   */
  GLOBAL_ADMIN = 50,

  /**
   * Level 60: System Admin
   * Sensika admin (can access source catalog and system/platform settings, subject to Feature Permissions)
   */
  SYSTEM_ADMIN = 60,

  /**
   * Level 70: Platform Admin
   * Platform admin (can create system roles and change user levels below 60)
   */
  PLATFORM_ADMIN = 70,

  /**
   * Level 80: Platform Owner
   * Platform owner (can set user levels <= 70)
   */
  PLATFORM_OWNER = 80,
}

export interface UserActivityResponse {
  count: number;
  logs: UserActivityLogEntry[];
  user: null;
}

export interface UserActivityLogEntry {
  id: number;
  time: {
    relative: string;
    absolute: string;
  };
  /** Canonical data-object ref (+ grid extras); null when the query is gone. */
  query: {
    id: string;
    object_class_id: number;
    object_type_id: number;
    name: string;
    lifecycle_status: number;
    workspace: { id: string | null };
    can_view: boolean;
    type: string;
    human_query: string;
  } | null;
  company: {
    id: string | null;
    object_class_id: number | null;
    lifecycle_status: number | null;
    name: string;
  };
  user: {
    id: string;
    email: string;
    name: string;
    avatar_url: string;
    image_url: string;
  };
  action: string;
  action_type_id: number;
}

// Define the mapped type
export type ExternalServiceMap = {
  [key in ExternalService]: {
    icon: IconProp;
    name: string;
    color: string; // color for the icon
  };
};

export const externalServiceMap: ExternalServiceMap = {
  [ExternalService.FACEBOOK]: {
    icon: ['fab', 'facebook'],
    name: 'Facebook',
    color: '#316FF6',
  },
  [ExternalService.MATTERMOST_MOE]: {
    icon: ['fas', 'turn-up'], // custom mattermost icon
    name: 'Mattermost Prod',
    color: '#0084cc',
  },
  [ExternalService.MATTERMOST_TEST]: {
    icon: ['fas', 'turn-up'], // custom mattermost icon
    name: 'Mattermost Test',
    color: '#0084cc',
  },
  [ExternalService.MATTERMOST_MOE_PROD]: {
    icon: ['fas', 'turn-up'], // custom mattermost icon
    name: 'Mattermost Prod',
    color: '#0084cc',
  },
};

/**
 * User client-side layer: per-session caches over the user lookup lists the
 * editors use, plus the user-domain types shared across the app. All backend
 * transport lives in `UserApiService` (and `ClientAdminApiService` /
 * `UtilsLogApiService` for the admin grids).
 */
@Injectable({
  providedIn: 'root',
})
export class UserService {
  // if you add new local cache variables, add them to clearCaches() function, so they could be cleared upon certain events
  private userGroupsCache = new Map<string, NomenclatureItem[]>();
  private userRolesCache = new Map<string, NomenclatureItem[]>();
  private userCompaniesCache = new Map<string, NomenclatureItem[]>();

  private userApi = inject(UserApiService);

  /**
   * Fetches user groups based on the provided query and context user ID.
   * Caches the result locally to avoid duplicate requests.
   * @param contextUserId The ID of the context user the groups are fetched for.
   * @param query The search query to filter user groups.
   * @returns An Observable that emits an array of NomenclatureItem objects representing the user groups.
   */
  fetchUserGroups(
    contextUserId: string,
    query: string,
  ): Observable<NomenclatureItem[]> {
    const cacheKey = `${contextUserId}_${query}`;

    // Check cache first
    if (this.userGroupsCache.has(cacheKey)) {
      return of(this.userGroupsCache.get(cacheKey)!);
    }

    return this.userApi.getAvailableUserGroups(contextUserId, query).pipe(
      tap((groups) => {
        // Cache the result
        this.userGroupsCache.set(cacheKey, groups);
      }),
    );
  }

  /**
   * Fetches user roles based on the provided query and context user ID.
   * Caches the result locally to avoid duplicate requests.
   * @param contextUserId The ID of the context user the roles are fetched for.
   * @param query The search query to filter user roles.
   * @returns An Observable that emits an array of NomenclatureItem objects representing the user roles.
   */
  fetchUserRoles(
    contextUserId: string,
    query: string,
  ): Observable<NomenclatureItem[]> {
    const cacheKey = `${contextUserId}_${query}`;

    // Check cache first
    if (this.userRolesCache.has(cacheKey)) {
      return of(this.userRolesCache.get(cacheKey)!);
    }

    return this.userApi.getAvailableUserRoles(contextUserId, query).pipe(
      tap((roles) => {
        // Cache the result
        this.userRolesCache.set(cacheKey, roles);
      }),
    );
  }

  /**
   * Fetches user companies based on the provided query and context user ID.
   * Caches the result locally to avoid duplicate requests.
   * @param contextUserId The ID of the context user the companies are fetched for.
   * @param query The search query to filter user companies.
   * @param includeExplicitlyPermitted TRUE to return requested user's explicitly permitted companies (respected if the current user is CA/GA/same as requested user)
   * @param includeImplicitlyPermitted TRUE to return requested user's implicitly permitted companies (respected if the current user is GA)
   * @returns An Observable that emits an array of NomenclatureItem objects representing the user companies.
   */
  fetchUserCompanies(
    contextUserId: string,
    query: string,
    includeExplicitlyPermitted: boolean = true,
    includeImplicitlyPermitted: boolean = true,
    enabledOnly: boolean = false,
  ): Observable<NomenclatureItem[]> {
    const cacheKey = `${contextUserId}_${query}_${includeExplicitlyPermitted}_${includeImplicitlyPermitted}_${enabledOnly}`;

    // Check cache first
    if (this.userCompaniesCache.has(cacheKey)) {
      return of(this.userCompaniesCache.get(cacheKey)!);
    }

    return this.userApi
      .getAvailableUserCompanies(
        contextUserId,
        query,
        includeExplicitlyPermitted,
        includeImplicitlyPermitted,
        enabledOnly,
      )
      .pipe(
        tap((companies) => {
          // Cache the result
          this.userCompaniesCache.set(cacheKey, companies);
        }),
      );
  }

  clearCaches() {
    // console.log('user-service: clear caches');
    this.userGroupsCache = new Map<string, NomenclatureItem[]>();
    this.userRolesCache = new Map<string, NomenclatureItem[]>();
    this.userCompaniesCache = new Map<string, NomenclatureItem[]>();
  }
}
